import React, { useState } from "react";
import {
  LocationInfo,
  GameCondition,
  TeamInfoResponse,
  Event,
  LocationInfo_Type,
  Coordinates,
  GameMode,
} from "../api/copenhunt/v1/copenhunt_pb";
import {
  Color,
  colorHexMap,
  Shape,
  shapeSvgMap,
  getTeamIconSvg,
} from "../utils";
import GameOverview from "./GameOverview";
import "../styles/UserInfoPanel.css";
import { lineString, point } from "@turf/helpers";
import polygonToLine from "@turf/polygon-to-line";
import pointToLineDistance from "@turf/point-to-line-distance";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { Feature, Polygon, LineString, FeatureCollection } from "geojson";

interface UserInfoPanelProps {
  teamColors: { [teamId: string]: Color };
  teamId: string;
  teamInfoById: { [teamId: string]: TeamInfoResponse };
  teamNumbers: { [teamId: string]: number };
  playerName: string;
  overview: {
    condition: GameCondition | undefined;
    secondsToNextSwitch: number | undefined;
    isPaused: boolean | undefined;
    roundDurationSeconds: number | undefined;
    secondsToNextPing?: number;
    pingIntervalSeconds?: number;
    events: Event[];
    latestPingTimestamp: number;
    currentTimestamp: number;
    mode: GameMode;
  };
  visibleLocations: LocationInfo[];
  objectiveId: string;
  objectiveShapes: { [objectiveId: string]: Shape };
  isHunter: boolean;
  onSendPing: () => void;
  playerCoordinates: Coordinates | null;
}

const UserInfoPanel: React.FC<UserInfoPanelProps> = ({
  teamColors,
  teamId,
  teamInfoById,
  teamNumbers,
  playerName,
  overview,
  visibleLocations,
  objectiveId,
  objectiveShapes,
  isHunter,
  onSendPing,
  playerCoordinates,
}) => {
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);
  const [isPinging, setIsPinging] = useState(false);
  const myTeamIconHtml = getTeamIconSvg(
    teamColors[teamId],
    teamNumbers[teamId],
    null
  );

  const handlePingClick = () => {
    if (isPinging) {
      return;
    }
    setIsPinging(true);
    onSendPing();
    setTimeout(() => {
      setIsPinging(false);
    }, 3000);
  };

  const pingsRemaining = teamInfoById[teamId]?.extraPingsRemaining ?? 0;
  const treasureLocations = visibleLocations.filter(
    (l) => l.type === LocationInfo_Type.TREASURE
  );

  const calculateDistance = (location: LocationInfo): string => {
    if (!playerCoordinates) {
      return "N/A";
    }

    try {
      const parsedGeoJson = JSON.parse(location.geoJson);

      let polygonFeature: Feature<Polygon> | undefined;

      if (parsedGeoJson.type === "FeatureCollection") {
        polygonFeature = parsedGeoJson.features.find(
          (f: Feature) => f.geometry.type === "Polygon"
        );
      } else if (
        parsedGeoJson.type === "Feature" &&
        parsedGeoJson.geometry.type === "Polygon"
      ) {
        polygonFeature = parsedGeoJson;
      }

      if (!polygonFeature) {
        return "N/A";
      }

      const playerPoint = point([
        playerCoordinates.longitude,
        playerCoordinates.latitude,
      ]);

      if (booleanPointInPolygon(playerPoint, polygonFeature)) {
        return "0m";
      }

      const line = polygonToLine(polygonFeature);
      if (!line) return "N/A";

      let distance = Infinity;

      if (line.type === "FeatureCollection") {
        // Input was MultiPolygon
        for (const feature of (
          line as FeatureCollection<LineString>
        ).features) {
          const d = pointToLineDistance(playerPoint, feature, {
            units: "meters",
          });
          if (d < distance) {
            distance = d;
          }
        }
      } else if (line.geometry.type === "LineString") {
        // Input was simple Polygon
        distance = pointToLineDistance(
          playerPoint,
          line as Feature<LineString>,
          {
            units: "meters",
          }
        );
      } else if (line.geometry.type === "MultiLineString") {
        // Input was Polygon with holes
        for (const lineCoords of line.geometry.coordinates) {
          const segment = lineString(lineCoords);
          const d = pointToLineDistance(playerPoint, segment, {
            units: "meters",
          });
          if (d < distance) {
            distance = d;
          }
        }
      } else {
        console.error("Unhandled output from polygonToLine");
        return "N/A";
      }

      if (distance === Infinity) {
        return "N/A";
      }

      return `${Math.round(distance)}m`;
    } catch (e) {
      console.error("Error calculating distance for location", location.id, e);
      return "N/A";
    }
  };

  return (
    <>
      <div className="notifications">
        <div className="legend">
          <div
            className="legend-item legend-toggle-row"
            onClick={() => setIsLegendExpanded(!isLegendExpanded)}
          >
            <div className="legend-item-main">
              <svg className="legend-icon" viewBox="6 6 24 24">
                <circle cx="18" cy="18" r="10" fill="#4285F4" />
                <text
                  x="18"
                  y="22"
                  textAnchor="middle"
                  fontSize="12"
                  fill="white"
                  fontWeight="bold"
                >
                  {teamNumbers[teamId]}
                </text>
              </svg>
              <span className="label" style={{ color: "#4285F4" }}>
                Me ({playerName})
              </span>
            </div>
            <svg
              className={`legend-arrow ${isLegendExpanded ? "expanded" : ""}`}
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path d="M7 10l5 5 5-5z" fill="currentColor" />
            </svg>
          </div>
          {isLegendExpanded && (
            <>
              <div className="legend-item">
                <svg
                  className="legend-icon"
                  viewBox="6 6 24 24"
                  dangerouslySetInnerHTML={{ __html: myTeamIconHtml }}
                />
                <span
                  className="label"
                  style={{
                    color:
                      colorHexMap[
                        teamColors[teamId] as keyof typeof colorHexMap
                      ],
                  }}
                >
                  My team ({teamInfoById[teamId].name})
                </span>
              </div>
              {Object.keys(teamColors)
                .filter((t) => teamId !== t)
                .map((t) => {
                  const teamIconHtml = getTeamIconSvg(
                    teamColors[t],
                    teamNumbers[t],
                    null
                  );
                  return (
                    <div className="legend-item" key={t}>
                      <svg
                        className="legend-icon"
                        viewBox="6 6 24 24"
                        dangerouslySetInnerHTML={{ __html: teamIconHtml }}
                      />
                      <span
                        className="label"
                        style={{
                          color:
                            colorHexMap[
                              teamColors[t] as keyof typeof colorHexMap
                            ],
                        }}
                      >
                        {teamInfoById[t].name}
                      </span>
                    </div>
                  );
                })}
            </>
          )}
        </div>
        <GameOverview
          condition={overview.condition}
          secondsToNextSwitch={overview.secondsToNextSwitch}
          isPaused={overview.isPaused}
          totalSeconds={overview.roundDurationSeconds}
          secondsToNextPing={overview.secondsToNextPing}
          pingIntervalSeconds={overview.pingIntervalSeconds}
          events={overview.events}
          latestPingTimestamp={overview.latestPingTimestamp}
          currentTimestamp={overview.currentTimestamp}
        />
        {!isHunter && treasureLocations.length > 0 && (
          <div className="objectives-section">
            <div className="locations-grid">
              {treasureLocations.map((location) => {
                const isGrabbed = objectiveId === location.id;
                const isUnavailable = objectiveId && !isGrabbed;
                const shape = objectiveShapes[location.id];
                const iconColor = isUnavailable ? "#ccc" : "#000000";
                const iconHtml = shape
                  ? shapeSvgMap[shape].replace("{color}", iconColor)
                  : "";

                return (
                  <div
                    key={location.id}
                    className={`location-container ${
                      isUnavailable ? "unavailable" : ""
                    }`}
                  >
                    <div
                      className={`location-item ${
                        isGrabbed ? "grabbed" : ""
                      }`}
                    >
                      <svg
                        className="location-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 36 36"
                        dangerouslySetInnerHTML={{ __html: iconHtml }}
                      />
                    </div>
                    {isGrabbed && (
                      <span className="location-label">Got it!</span>
                    )}
                    {overview.mode === GameMode.DISTANCES_ONLY &&
                      !isGrabbed && (
                        <span className="distance-text">
                          {calculateDistance(location)}
                        </span>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {isHunter && (
        <div className="notifications">
          <div className="ping-button-container">
            <div className="ping-counter">{pingsRemaining}</div>
            <button
              className="ping-button"
              onClick={handlePingClick}
              disabled={isPinging || pingsRemaining === 0}
            >
              {isPinging ? "Pinged!" : "Extra ping!"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default UserInfoPanel;
